// STAGING-LAUNCH-READINESS-002-R2 — customer cabinet: order detail + "Чек і документи".
// STAGING-LAUNCH-READINESS-002-R2-R2 — seed from cache, bounded load, 8s degraded
// (Retry + Home) fallback instead of an infinite spinner.
import { useEffect, useRef, useState } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import {
  fetchOrderDetailBounded,
  getCachedOrderDetail,
  cancelCustomerOrder,
  invalidateOrderDetailCache,
  type CustomerOrderDetailResponse,
  type CustomerOrderItem,
} from "./order-history-v2.js";
import { resolveIdentityRows } from "./product-identity-v2.js";
import {
  paymentStatusLabel,
  receiptStateLabel,
  deliveryStateLabel,
} from "./order-status-copy-v2.js";
import {
  canonicalDisplayLabelUk,
  hideCustomerPaymentCta,
  projectCanonicalDisplayState,
} from "./canonical-display-state-v2.js";
import { presentCustomerTimeline } from "./order-timeline-present-v2.js";
import type { ReorderSourceInput } from "./reorder-v2.js";
import { formatUah } from "./money-v2.js";
import { openHttpsSync } from "./external-link-v2.js";
import { LoadingBlock, EmptyState, RouteDegradedState, OrderItemThumb } from "./components.js";
import { useRouteLoadingGate } from "./route-loading-gate-v2.js";
import { emitEvent } from "./events-v2.js";
import { OrderPaymentPanelV2 } from "./OrderPaymentPanelV2.js";
import { customerOrderActionV2 } from "./order-actions-v2.js";
import { pollAsiaStyleBankPaymentStatus } from "./bank-iban-payment-v2.js";
import { getNovapayPaymentStatusV2, novapayStatusCopy } from "./novapay-checkout-v2.js";
import { getCustomerOrderActions, isPaidPaymentStatus } from "./customer-order-actions-registry-v2.js";
import { CustomerOrderActionsBarV2 } from "./CustomerOrderActionsBarV2.js";
import {
  PaymentMethodChangeSheetV2,
  type PaymentMethodChoiceId,
} from "./PaymentMethodChangeSheetV2.js";
import { enabledCustomerCheckoutPaymentMethods } from "./customer-payment-flags-v2.js";
import {
  CUSTOMER_CANCEL_REASON_SELECTABLE,
  CUSTOMER_CANCEL_REASON_EN,
  CUSTOMER_CANCEL_REASON_UK,
  cancelRescueKind,
  type CustomerCancelReasonCode,
} from "./customer-cancel-reasons-v2.js";
import { isNovapayCheckoutDegradedStaging } from "./novapay-staging-degraded-v2.js";
import { t } from "./store-v2-ui-copy.js";
import { formatOrderReceivedCardTime, getOrderReceivedAt } from "./order-received-at.js";
import { FiscalReceiptCardV2 } from "./FiscalReceiptCardV2.js";
import { ReferralCardV2, type ReferralShareData } from "./ReferralCardV2.js";
import { humanDeliveryMethodLabel } from "./delivery-method-label-v2.js";
import { resolveCustomerDeliveryEditEligibility } from "./customer-delivery-edit-v2.js";
import { updateCustomerDeliveryV2 } from "./update-delivery-v2.js";
import { openCustomerDocumentPdf, mintCustomerDocumentViewUrl } from "./customer-document-open-v2.js";
import { parseCustomerDocumentUrl } from "./customer-document-access-v2.js";
import { DocumentPdfViewerV2 } from "./DocumentPdfViewerV2.js";
import {
  DocumentNativeReader,
  usePreferNativeDocumentReader,
  type DocumentViewModelClient,
} from "./DocumentNativeReader.js";
import { storeV2AuthHeaders } from "./store-v2-auth-headers-v2.js";
import {
  canonicalOrderExplanation,
  canonicalOrderTitle,
  mapToCanonicalCustomerStage,
} from "./canonical-order-projection-v2.js";

function formatOrderDate(iso: string, lang: StoreV2Lang): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(lang === "en" ? "en-GB" : "uk-UA", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function OrderDetailV2({
  lang,
  sessionKey,
  orderId,
  referral = null,
  onReferralShared,
  onRepeatOrder,
  onContactManager,
  onGoHome,
}: {
  lang: StoreV2Lang;
  sessionKey: string;
  orderId: string;
  referral?: ReferralShareData | null;
  onReferralShared?: () => void;
  onRepeatOrder: (source: ReorderSourceInput) => void;
  onContactManager: () => void;
  onGoHome: () => void;
}) {
  const en = lang === "en";
  const [data, setData] = useState<CustomerOrderDetailResponse | null>(() =>
    getCachedOrderDetail(sessionKey, orderId),
  );
  const [hardError, setHardError] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const isLoading = data == null && !hardError && !notFound;
  const { showSkeleton, timedOut } = useRouteLoadingGate(isLoading);

  // STAGING-LAUNCH-READINESS-002-R2-R2 — cancel-order confirmation sheet + result state.
  const [cancelSheetOpen, setCancelSheetOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelBlockedMsg, setCancelBlockedMsg] = useState<string | null>(null);
  const [cancelledNow, setCancelledNow] = useState(false);
  // «Змінити спосіб» / «Допомога» — direct customer actions, decoupled from the
  // embedded NovaPay recovery panel (which only owns the hosted-payment CTA here).
  const [methodBusy, setMethodBusy] = useState(false);
  const [methodMsg, setMethodMsg] = useState<string | null>(null);
  const [methodSaveError, setMethodSaveError] = useState<string | null>(null);
  const [providerProcessing, setProviderProcessing] = useState(false);
  const [processingConfirmOpen, setProcessingConfirmOpen] = useState(false);
  const [pendingMethodChange, setPendingMethodChange] = useState<{
    next: PaymentMethodChoiceId;
    meta?: { correlationId: string; durationMs: number } | undefined;
  } | null>(null);
  const [statusCheckBusy, setStatusCheckBusy] = useState(false);
  const [statusCheckMsg, setStatusCheckMsg] = useState<string | null>(null);
  const [methodSheetOpen, setMethodSheetOpen] = useState(false);
  const [abandonBusy, setAbandonBusy] = useState(false);
  const [abandonMsg, setAbandonMsg] = useState<string | null>(null);
  const [selectedReasonChip, setSelectedReasonChip] = useState<CustomerCancelReasonCode | null>(null);
  const [cancelRequestedNow, setCancelRequestedNow] = useState(false);
  const [deliveryEditOpen, setDeliveryEditOpen] = useState(false);
  const [deliveryEditBusy, setDeliveryEditBusy] = useState(false);
  const [deliveryEditMsg, setDeliveryEditMsg] = useState<string | null>(null);
  const [editCity, setEditCity] = useState("");
  const [editWarehouse, setEditWarehouse] = useState("");
  const [editName, setEditName] = useState("");
  const [pdfViewer, setPdfViewer] = useState<{
    httpsUrl: string;
    titleUk: string;
    documentNumber: string | null;
    sourceUrl: string;
  } | null>(null);
  const [nativeReader, setNativeReader] = useState<{
    view: DocumentViewModelClient | null;
    loading: boolean;
    error: string | null;
    sourceUrl: string;
    titleUk: string;
    documentNumber: string | null;
  } | null>(null);
  const preferNativeReader = usePreferNativeDocumentReader();
  const [trackingPanel, setTrackingPanel] = useState<{
    ttn: string;
    trackUrl: string | null;
    status: string | null;
    updatedAt: string | null;
    slot: number;
    timeline: Array<{ event_type: string; created_at: string }>;
    historyLocalOnly: boolean;
  } | null>(null);
  const payCtaRef = useRef<(() => void) | null>(null);
  const fopReceiptCtaRef = useRef<(() => void) | null>(null);
  const [detailTab, setDetailTab] = useState<"overview" | "items" | "payment" | "documents">("overview");

  // R5.7 — Telegram/system Back closes document overlays as one logical step
  useEffect(() => {
    if (!nativeReader && !pdfViewer) return;
    const webApp = (
      window as unknown as {
        Telegram?: {
          WebApp?: {
            BackButton?: {
              show: () => void;
              hide: () => void;
              onClick: (cb: () => void) => void;
              offClick?: (cb: () => void) => void;
            };
          };
        };
      }
    ).Telegram?.WebApp;
    if (!webApp?.BackButton) return;
    const handler = () => {
      if (pdfViewer) {
        setPdfViewer(null);
        return;
      }
      setNativeReader(null);
    };
    webApp.BackButton.show();
    webApp.BackButton.onClick(handler);
    return () => {
      if (typeof webApp.BackButton?.offClick === "function") {
        webApp.BackButton.offClick(handler);
      }
    };
  }, [nativeReader, pdfViewer]);

  const REASON_CHIPS = CUSTOMER_CANCEL_REASON_SELECTABLE.map((code) => ({
    code,
    label: en ? CUSTOMER_CANCEL_REASON_EN[code] : CUSTOMER_CANCEL_REASON_UK[code],
  }));
  const rescue = cancelRescueKind(selectedReasonChip);
  const allowedMethodsCount = enabledCustomerCheckoutPaymentMethods().length;

  const resolveCancelReason = () => {
    const free = cancelReason.trim();
    if (selectedReasonChip && selectedReasonChip !== "other") {
      const label = en
        ? CUSTOMER_CANCEL_REASON_EN[selectedReasonChip]
        : CUSTOMER_CANCEL_REASON_UK[selectedReasonChip];
      return free ? `${label}: ${free}` : label;
    }
    return free || (selectedReasonChip ? CUSTOMER_CANCEL_REASON_UK.other : undefined);
  };

  useEffect(() => {
    emitEvent("order_detail_opened", { order_id_present: true });
    // COMMERCE-FISCAL-161A-R1 — deep link / payret → scroll receipt block into view.
    const t = window.setTimeout(() => {
      document.querySelector('[data-testid="sv2-receipt-section"]')?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }, 350);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const cached = getCachedOrderDetail(sessionKey, orderId);
    if (cached) setData(cached);
    invalidateOrderDetailCache(sessionKey, orderId);
    void fetchOrderDetailBounded(sessionKey, orderId).then((res) => {
      if (cancelled) return;
      if (res.ok) {
        setData(res.data);
        setHardError(false);
        setNotFound(false);
        emitEvent("receipt_section_viewed", { receipt_state: res.data.receipt.state });
      } else if (!cached) {
        if (res.notFound) setNotFound(true);
        else setHardError(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sessionKey, orderId, attempt]);

  useEffect(() => {
    if (data?.order.payment_method !== "bank_transfer_fop") return;
    if (isPaidPaymentStatus(data.order.payment_status, data.order.bank_confirmed_at ?? null)) return;
    let cancelled = false;
    const tick = async () => {
      const result = await pollAsiaStyleBankPaymentStatus({ sessionKey, orderId });
      if (cancelled || !result.paid) return;
      invalidateOrderDetailCache(sessionKey, orderId);
      setAttempt((n) => n + 1);
    };
    void tick();
    const onVis = () => {
      if (document.visibilityState === "visible") void tick();
    };
    document.addEventListener("visibilitychange", onVis);
    const timer = window.setInterval(() => void tick(), 8_000);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      window.clearInterval(timer);
    };
  }, [sessionKey, orderId, data?.order.payment_method, data?.order.payment_status]);

  const retry = () => {
    setHardError(false);
    setNotFound(false);
    setAttempt((n) => n + 1);
  };

  if (data === null) {
    if (notFound) {
      return (
        <div className="sv2-body sv2-route-top" data-route-top="order-detail">
          <EmptyState
            emoji="⚠️"
            title={en ? "Order not found" : "Замовлення не знайдено"}
            hint={
              en
                ? "It may belong to a different session, or the link is invalid."
                : "Можливо, воно належить іншій сесії, або посилання недійсне."
            }
          />
        </div>
      );
    }
    if (timedOut || hardError) {
      return (
        <div className="sv2-body sv2-route-top" data-route-top="order-detail">
          <RouteDegradedState lang={lang} onRetry={retry} onHome={onGoHome} />
        </div>
      );
    }
    return (
      <div className="sv2-body sv2-route-top" data-route-top="order-detail">
        {showSkeleton ? <LoadingBlock label={en ? "Loading order…" : "Завантажуємо замовлення…"} /> : null}
      </div>
    );
  }

  const { order, items, receipt, delivery, timeline } = data;
  const projection = data.canonical_projection;
  const isCancelledOrder = order.order_top_status === "canceled" || order.order_top_status === "cancelled" || cancelledNow;
  const displayState = projectCanonicalDisplayState({
    order_top_status: isCancelledOrder ? "canceled" : order.order_top_status,
    payment_status: order.payment_status,
    payment_method: order.payment_method,
    ttn_status: order.ttn_status,
    display_state: data.display_state ?? projection?.display_state ?? null,
    payment_timing: (order as { payment_timing?: string | null }).payment_timing ?? null,
    bank_confirmed_at: (order as { bank_confirmed_at?: string | null }).bank_confirmed_at ?? null,
  });
  const isBankIban =
    order.payment_method === "bank_transfer_fop" ||
    /iban|fop/i.test(String(order.payment_method ?? ""));
  const hideFiscalBlock = isBankIban;
  const deliveryEdit = resolveCustomerDeliveryEditEligibility({
    orderTopStatus: order.order_top_status,
    status: order.status,
    paymentStatus: order.payment_status,
    ttn: delivery.ttn_number ?? delivery.state,
  });
  const bankConfirmedAt = (order as { bank_confirmed_at?: string | null }).bank_confirmed_at ?? null;
  const statusRail =
    (order as { status_rail?: string }).status_rail ??
    (isCancelledOrder
      ? "cancelled"
      : order.order_top_status === "completed"
        ? "completed"
        : receipt.state === "ready" || receipt.state === "fiscalized"
          ? "paid"
          : String(order.payment_status ?? "").startsWith("paid") || Boolean(String(bankConfirmedAt ?? "").trim())
            ? "paid"
            : "awaiting");
  const hasTtn = delivery.state === "ttn_created" && Boolean(delivery.ttn_number);
  const packingOrShipmentStarted =
    hasTtn ||
    ["packing", "ready_to_ship", "shipped", "completed", "delivered", "received", "collected"].includes(
      String(order.order_top_status ?? order.status ?? ""),
    );
  const isPaid = isPaidPaymentStatus(order.payment_status, bankConfirmedAt);
  const isUnpaid = !isCancelledOrder && !isPaid;
  const isNovapayUnpaid = isUnpaid && order.payment_method === "novapay_card";
  const paymentMethodForPanel =
    order.payment_method === "bank_transfer_fop" ||
    order.payment_method === "nova_poshta_cod" ||
    order.payment_method === "novapay_card"
      ? order.payment_method
      : "novapay_card";
  const actionPlan = getCustomerOrderActions({
    orderStatus: cancelledNow ? "canceled" : order.status,
    orderTopStatus: cancelledNow ? "canceled" : order.order_top_status,
    paymentStatus: isPaid ? "payment_confirmed_bank_iban" : order.payment_status,
    paymentMethod: order.payment_method,
    fulfillmentStatus: order.order_top_status,
    packingOrShipmentStarted,
    routeContext: "order_detail",
    novapayCheckoutDegraded: isNovapayCheckoutDegradedStaging(),
    providerProcessing,
    allowedMethodsCount,
  });
  const visiblePlan = {
    ...actionPlan,
    // Payment panel owns the single FOP receipt CTA — never duplicate in actions bar.
    actions: actionPlan.actions.filter((id) => id !== "send_receipt"),
  };

  const runCancel = async () => {
    if (cancelBusy) return;
    setCancelBusy(true);
    setCancelBlockedMsg(null);
    try {
      const res = await cancelCustomerOrder(sessionKey, orderId, resolveCancelReason(), {
        reasonCode: selectedReasonChip,
        requestOnly: isPaid,
      });
      if (res.ok) {
        if (isPaid) {
          setCancelRequestedNow(true);
        } else {
          setCancelledNow(true);
        }
        setCancelSheetOpen(false);
        invalidateOrderDetailCache(sessionKey, orderId);
        emitEvent("order_cancelled_by_customer", {
          order_id_present: true,
          reason_present: Boolean(resolveCancelReason()),
          reason_chip: selectedReasonChip,
          reason_code: selectedReasonChip,
          request_only: isPaid,
        });
        setAttempt((n) => n + 1);
      } else {
        setCancelSheetOpen(false);
        setCancelBlockedMsg(
          res.message_uk ?? (en ? "Could not cancel the order. Try again or contact the manager." : "Не вдалося скасувати замовлення. Спробуйте ще раз або напишіть менеджеру."),
        );
      }
    } finally {
      setCancelBusy(false);
    }
  };

  const runChangeMethod = async (
    next: PaymentMethodChoiceId,
    meta?: { correlationId: string; durationMs: number },
    opts?: { confirmProcessingAbandon?: boolean },
  ) => {
    if (methodBusy) return;
    setMethodBusy(true);
    setMethodMsg(null);
    setMethodSaveError(null);
    const started = Date.now();
    const res = await customerOrderActionV2({
      sessionKey,
      orderId,
      action: "change_payment_method",
      paymentMethod: next,
      paymentComment: selectedReasonChip || cancelReason.trim() || null,
      confirmProcessingAbandon: opts?.confirmProcessingAbandon === true,
    });
    setMethodBusy(false);
    if (res.ok) {
      invalidateOrderDetailCache(sessionKey, orderId);
      setMethodSheetOpen(false);
      setProcessingConfirmOpen(false);
      setPendingMethodChange(null);
      emitEvent("payment_method_change_succeeded", {
        order_status: order.status,
        current_method: order.payment_method,
        selected_method: next,
        duration_ms: meta?.durationMs ?? Date.now() - started,
        correlation_id: meta?.correlationId ?? null,
      });
      emitEvent("payment_method_changed_by_customer", { payment_method: next });
      setAttempt((n) => n + 1);
    } else if (res.error === "provider_still_processing") {
      setPendingMethodChange({ next, ...(meta ? { meta } : {}) });
      setProcessingConfirmOpen(true);
      setMethodSaveError(res.message ?? t(lang, "confirm_abandon_processing_body"));
    } else if (res.error === "already_paid") {
      setMethodSaveError(res.message ?? (en ? "Order is already paid." : "Замовлення вже оплачене."));
      setAttempt((n) => n + 1);
    } else {
      const msg =
        res.message ?? (en ? "Could not change payment method." : "Не вдалося змінити спосіб оплати.");
      setMethodSaveError(msg);
      setMethodMsg(msg);
      emitEvent("payment_method_change_failed", {
        order_status: order.status,
        current_method: order.payment_method,
        selected_method: next,
        error_code: res.error ?? "unknown",
        duration_ms: meta?.durationMs ?? Date.now() - started,
        correlation_id: meta?.correlationId ?? null,
      });
    }
  };

  const runAbandonPayment = async () => {
    if (abandonBusy) return;
    setAbandonBusy(true);
    setAbandonMsg(null);
    const reason = resolveCancelReason();
    const res = await customerOrderActionV2({
      sessionKey,
      orderId,
      action: "abandon_payment",
      paymentComment: reason ?? null,
    });
    setAbandonBusy(false);
    if (res.ok) {
      invalidateOrderDetailCache(sessionKey, orderId);
      emitEvent("payment_abandoned_by_customer", {
        reason_present: Boolean(reason),
        reason_chip: selectedReasonChip,
      });
      setAbandonMsg(
        res.message ??
          (en
            ? "Payment attempt cancelled. Order kept — you can pay later or change method."
            : "Спробу оплати скасовано. Замовлення збережено — можете оплатити пізніше або змінити спосіб."),
      );
      setAttempt((n) => n + 1);
    } else {
      setAbandonMsg(res.message ?? (en ? "Could not cancel the payment attempt." : "Не вдалося відмінити оплату."));
    }
  };

  const runStatusCheck = async () => {
    if (statusCheckBusy) return;
    setStatusCheckBusy(true);
    setStatusCheckMsg(null);
    const res = await getNovapayPaymentStatusV2({ orderId, sessionKey });
    setStatusCheckBusy(false);
    if (res.ok && res.payment?.status) {
      const prov = String(res.payment.provider_status ?? "").toLowerCase();
      const processing =
        res.payment.provider_truth_state === "provider_processing" ||
        prov === "processing" ||
        prov.includes("processing");
      setProviderProcessing(processing);
      setStatusCheckMsg(
        processing
          ? t(lang, "payment_novapay_processing")
          : novapayStatusCopy(lang, res.payment.status),
      );
      if (res.payment.status === "paid") setAttempt((n) => n + 1);
    } else {
      setStatusCheckMsg(en ? "Could not refresh status." : "Не вдалося оновити статус.");
    }
  };

  return (
    <div className="sv2-body sv2-route-top" data-route-top="order-detail" data-testid="sv2-order-detail" data-rail={statusRail} id="sv2-order-section-overview">
      <div className="sv2-info-card sv2-cabinet-card" data-rail={statusRail}>
        <div className="sv2-order-list-row">
          <strong>№ {order.display_order_number}</strong>
          <span className="sv2-muted">
            {formatOrderReceivedCardTime(getOrderReceivedAt(order as never))}
          </span>
        </div>
        <div className="sv2-status-line">
          <span>{en ? "Payment" : "Оплата"}:</span>
          <strong data-testid="sv2-order-payment-status">
            {displayState === "CANCELLED"
              ? en
                ? "Cancelled"
                : "Скасовано"
              : displayState === "REFUND_PENDING"
                ? en
                  ? "Cancellation / refund requested"
                  : "Запит на скасування / повернення"
                : isPaid
                  ? en
                    ? "Confirmed"
                    : "Підтверджено"
                  : displayState === "AWAITING_STOCK_CONFIRMATION" ||
                      displayState === "AWAITING_BANK_CONFIRMATION" ||
                      displayState === "AWAITING_MANAGER_ACCEPTANCE"
                    ? canonicalDisplayLabelUk(displayState)
                    : paymentStatusLabel(lang, order.payment_status, order.payment_method, {
                        ttn_status: order.ttn_status ?? null,
                      })}
          </strong>
        </div>
        {isPaid && order.payment_method === "bank_transfer_fop" && bankConfirmedAt ? (
          <p className="sv2-muted" style={{ margin: "6px 0 0", fontSize: 13 }} data-testid="sv2-bank-paid-copy">
            {formatOrderReceivedCardTime(bankConfirmedAt)}
            {order.total_uah != null ? ` · ${formatUah(order.total_uah)}` : ""}
          </p>
        ) : displayState === "REFUND_PENDING" ? (
          <p className="sv2-muted" style={{ margin: "6px 0 0", fontSize: 13 }} data-testid="sv2-refund-pending-copy">
            {en
              ? "We could not confirm availability of one or more items. We will refund your payment."
              : "Не вдалося підтвердити наявність одного або кількох товарів. Ми повернемо кошти."}
          </p>
        ) : displayState === "CANCELLED" ? (
          <p className="sv2-muted" style={{ margin: "6px 0 0", fontSize: 13 }} data-testid="sv2-cancelled-copy">
            {en ? "The order was cancelled." : "Замовлення скасовано"}
          </p>
        ) : null}
        {(data.payment_evidence ?? []).length > 0 ? (
          <div className="sv2-info-card" style={{ marginTop: 10 }} data-testid="sv2-payment-evidence">
            {(data.payment_evidence ?? []).map((ev) => (
              <div key={ev.receipt_id} className="sv2-order-list-row" style={{ alignItems: "center" }}>
                <span>📄 {en ? "Receipt" : "Квитанція"}</span>
                <span className="sv2-muted">{formatOrderDate(ev.created_at, lang)}</span>
                <a
                  href={`/api/store-v2/payment-evidence?order_id=${encodeURIComponent(order.order_id)}&receipt_id=${encodeURIComponent(ev.receipt_id)}&session_key=${encodeURIComponent(sessionKey)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="sv2-cta sv2-secondary"
                  style={{ padding: "6px 10px", fontSize: 13 }}
                >
                  {en ? "View" : "Переглянути"}
                </a>
              </div>
            ))}
          </div>
        ) : null}
        {hideFiscalBlock ? null : (
        <div className="sv2-status-line">
          <span>{en ? "Fiscalization" : "Фіскалізація"}:</span>
          <strong>
            {receipt.state === "ready" || receipt.state === "fiscalized"
              ? en
                ? "✓ Fiscal receipt created"
                : "✓ Фіскальний чек сформовано"
              : receiptStateLabel(lang, receipt.state)}
          </strong>
        </div>
        )}
        <div className="sv2-status-line">
          <span>{en ? "Order status" : "Статус замовлення"}:</span>
          <strong data-testid="sv2-order-canonical-status">
            {(() => {
              const stage = mapToCanonicalCustomerStage({
                order_top_status: order.order_top_status ?? null,
                status: order.status ?? null,
                primary_order_state: projection?.primary_order_state ?? null,
                ttn_status: order.ttn_status ?? null,
                delivery_status: delivery.state ?? null,
                ttn_number: delivery.ttn_number ?? null,
              });
              if (stage === "CANCELLED") return en ? "Cancelled" : "Скасовано";
              if (displayState === "REFUND_PENDING") {
                return en ? "Refund pending" : "Запит на скасування / повернення";
              }
              return canonicalOrderTitle(lang, stage);
            })()}
          </strong>
        </div>
        {isCancelledOrder ? (
          <div data-testid="sv2-order-cancelled-block" style={{ marginTop: 8 }}>
            {data.cancel_customer_reason ? (
              <p className="sv2-muted" style={{ margin: 0 }}>
                {en ? "Reason" : "Причина"}: {data.cancel_customer_reason}
              </p>
            ) : null}
            {data.cancelled_at ? (
              <p className="sv2-muted" style={{ margin: "4px 0 0" }}>
                {formatOrderDate(data.cancelled_at, lang)}
              </p>
            ) : null}
          </div>
        ) : null}
        {(() => {
          const stage = mapToCanonicalCustomerStage({
            order_top_status: order.order_top_status ?? null,
            status: order.status ?? null,
            primary_order_state: projection?.primary_order_state ?? null,
            ttn_status: order.ttn_status ?? null,
          });
          const expl = canonicalOrderExplanation(lang, stage);
          return expl ? (
            <p className="sv2-muted" style={{ fontSize: 13, marginTop: 8 }} data-testid="sv2-order-status-expl">
              {expl}
            </p>
          ) : null;
        })()}
      </div>

      <nav
        className="sv2-order-section-tabs"
        data-testid="sv2-order-section-tabs"
        aria-label={en ? "Order sections" : "Розділи замовлення"}
        style={{
          display: "flex",
          gap: 8,
          overflowX: "auto",
          marginTop: 12,
          paddingBottom: 4,
          WebkitOverflowScrolling: "touch",
        }}
      >
        {(
          [
            ["overview", en ? "Overview" : "Огляд"],
            ["items", en ? "Items" : "Товари"],
            ["payment", en ? "Payment" : "Оплата"],
            ["documents", en ? "Documents" : "Документи"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className="sv2-chip"
            data-testid={`sv2-order-tab-${id}`}
            aria-current={detailTab === id ? "page" : undefined}
            onClick={() => {
              setDetailTab(id);
              document.getElementById(`sv2-order-section-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            {label}
          </button>
        ))}
      </nav>

      <div className="sv2-section-title" style={{ marginTop: 16 }} id="sv2-order-section-items">
        {en ? "Items" : "Товари"}
      </div>
      <div className="sv2-info-card sv2-cabinet-card">
        {items.map((item) => {
          const idRows = resolveIdentityRows(en ? "en" : "ua", {
            merchantSku: item.sku,
            barcodeGtin: item.barcode,
            manufacturerPartNumber: item.original_sku,
            originalSkuVerified: Boolean(item.original_sku),
          });
          return (
          <div key={item.product_id} className="sv2-order-list-row" data-testid="sv2-order-item">
            <OrderItemThumb imageUrl={item.image_url} alt={item.title} className="sv2-order-item-img" />
            <span className="sv2-order-item-text">
              <span>
                {item.title} × {item.qty}
              </span>
              {idRows.map((row) => (
                <span
                  key={`${row.kind}-${row.value}`}
                  className="sv2-muted sv2-order-item-sku"
                  data-testid={`sv2-order-item-${row.kind}`}
                >
                  {row.label}: {row.value}
                </span>
              ))}
            </span>
            <span>{item.line_total_uah != null ? formatUah(item.line_total_uah) : "—"}</span>
          </div>
          );
        })}
        <div className="sv2-order-list-row" style={{ marginTop: 8, borderTop: "1px solid var(--sv2-border, #eee)", paddingTop: 8 }}>
          <strong>{en ? "Total" : "Разом"}</strong>
          <strong>{order.total_uah != null ? formatUah(order.total_uah) : "—"}</strong>
        </div>
      </div>

        <div className="sv2-info-card sv2-cabinet-card" data-testid="sv2-documents-section" id="sv2-order-section-documents" style={{ marginTop: 12 }}>
          <div className="sv2-section-title" style={{ margin: "0 0 6px" }}>
            {t(lang, "documents_section")}
          </div>
          {(data.documents ?? []).length > 0 ? (
            <>
              <div
                className="sv2-doc-package-header"
                data-testid="sv2-doc-package-header"
                style={{
                  marginBottom: 10,
                  padding: "10px 12px",
                  borderRadius: 12,
                  borderLeft: "4px solid #16a34a",
                  background: "var(--sv2-secbg, #f3f4f6)",
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14 }}>
                  {String(data.order.display_order_number ?? "").trim() || "—"}
                </div>
                <div className="sv2-muted" style={{ fontSize: 12, marginTop: 2 }}>
                  {(data.documents ?? []).length} {en ? "documents" : "документи"}
                </div>
                <ul style={{ margin: "6px 0 0", padding: 0, listStyle: "none", fontSize: 13 }}>
                  {(data.documents ?? []).map((d, i) => (
                    <li key={`pkg-${d.document_id ?? i}`}>
                      {d.url ? "✓" : "○"}{" "}
                      {d.title_uk ||
                        (d.type === "INVOICE"
                          ? en
                            ? "Invoice"
                            : "Рахунок"
                          : d.type === "PAYMENT_CONFIRMATION"
                            ? en
                              ? "Payment confirmation"
                              : "Підтвердження оплати"
                            : d.type === "SALES_DOCUMENT"
                              ? en
                                ? "Delivery note"
                                : "Видаткова накладна"
                              : en
                                ? "Document"
                                : "Документ")}
                    </li>
                  ))}
                </ul>
              </div>
            <ul
              className="sv2-muted"
              style={{ margin: 0, paddingLeft: 0, listStyle: "none", fontSize: 13 }}
              data-testid="sv2-documents-list"
            >
              {(data.documents ?? []).map((doc, idx) => {
                const icon =
                  doc.type === "INVOICE"
                    ? "📄"
                    : doc.type === "PAYMENT_CONFIRMATION"
                    ? "✅"
                    : doc.type === "SALES_DOCUMENT"
                      ? "📦"
                      : doc.type === "FISCAL_RECEIPT"
                        ? "🧾"
                        : doc.type === "REFUND_DOCUMENT"
                          ? "💸"
                          : "📄";
                const title =
                  doc.title_uk ||
                  (doc.type === "INVOICE"
                    ? en
                      ? "Invoice"
                      : "Рахунок на оплату"
                    : doc.type === "PAYMENT_CONFIRMATION"
                      ? en
                        ? "Payment confirmation"
                        : "Підтвердження оплати"
                      : doc.type === "SALES_DOCUMENT"
                        ? en
                          ? "Delivery note"
                          : "Видаткова накладна"
                        : en
                          ? "Document"
                          : "Документ");
                const chipRaw = doc.status_chip ?? null;
                const hasOpenableArtifact = Boolean(doc.url && String(doc.url).trim());
                // LAUNCH_R1_NO_FALSE_DOCUMENT_ACTION — never show «Доступний» / PDF CTAs without a real open URL.
                const chip = hasOpenableArtifact
                  ? chipRaw === "available" || doc.status === "ready"
                    ? "available"
                    : chipRaw ?? "created"
                  : chipRaw === "awaiting_signature" ||
                      chipRaw === "signed" ||
                      chipRaw === "rejected"
                    ? chipRaw
                    : "created";
                const chipUk =
                  doc.status_uk && hasOpenableArtifact && chip === "available"
                    ? doc.status_uk
                    : chip === "available"
                      ? "Доступний"
                      : chip === "awaiting_signature"
                        ? "Очікує підпису"
                        : chip === "signed"
                          ? "Підписано"
                          : chip === "rejected"
                            ? "Відхилено"
                            : en
                              ? "Preparing"
                              : "Готується";
                const stg = String(doc.public_order_number ?? data.order.display_order_number ?? "").trim();
                return (
                  <li
                    key={`${doc.type}-${doc.document_id ?? idx}`}
                    data-doc-type={doc.type}
                    style={{ marginBottom: 10 }}
                  >
                    <div style={{ fontWeight: 600 }}>
                      {icon} {title}{" "}
                      <span className={`sv2-doc-status-chip sv2-doc-status-chip--${chip}`} data-testid="sv2-doc-status-chip">
                        [{chipUk}]
                      </span>
                    </div>
                    {doc.document_number ? (
                      <div className="sv2-muted" style={{ fontSize: 12 }}>
                        № {doc.document_number}
                      </div>
                    ) : null}
                    {stg.startsWith("STG-") ? (
                      <div className="sv2-muted" style={{ fontSize: 12 }} data-testid="sv2-doc-stg">
                        {en ? "Order" : "Замовлення"} {stg}
                      </div>
                    ) : null}
                    {hasOpenableArtifact ? (
                      <div className="sv2-doc-actions" data-testid="sv2-doc-actions">
                        <button
                          type="button"
                          className="sv2-cta sv2-secondary"
                          data-testid="sv2-doc-open"
                          onClick={() => {
                            const titleUk = String(doc.title_uk ?? doc.type ?? "Документ");
                            const documentNumber = doc.document_number ?? null;
                            const sourceUrl = doc.url!;
                            const openPdf = () => {
                              void mintCustomerDocumentViewUrl({ url: sourceUrl, lang }).then((r) => {
                                if (!r.ok) {
                                  if (r.message) window.alert(r.message);
                                  return;
                                }
                                setNativeReader(null);
                                setPdfViewer({
                                  httpsUrl: r.httpsUrl,
                                  titleUk,
                                  documentNumber,
                                  sourceUrl,
                                });
                              });
                            };
                            if (!preferNativeReader) {
                              openPdf();
                              return;
                            }
                            const parsed = parseCustomerDocumentUrl(sourceUrl);
                            if (!parsed) {
                              openPdf();
                              return;
                            }
                            setNativeReader({
                              view: null,
                              loading: true,
                              error: null,
                              sourceUrl,
                              titleUk,
                              documentNumber,
                            });
                            void fetch(
                              `/api/store-v2/order-document-view?order_id=${encodeURIComponent(parsed.orderId)}&document_id=${encodeURIComponent(parsed.documentId)}&session_key=${encodeURIComponent(parsed.sessionKey)}`,
                              { credentials: "same-origin", headers: storeV2AuthHeaders() },
                            )
                              .then(async (res) => {
                                const data = (await res.json()) as {
                                  ok?: boolean;
                                  view?: DocumentViewModelClient;
                                  error?: string;
                                };
                                if (!res.ok || !data.ok || !data.view) {
                                  setNativeReader(null);
                                  openPdf();
                                  return;
                                }
                                setNativeReader({
                                  view: data.view,
                                  loading: false,
                                  error: null,
                                  sourceUrl,
                                  titleUk,
                                  documentNumber,
                                });
                              })
                              .catch(() => {
                                setNativeReader(null);
                                openPdf();
                              });
                          }}
                        >
                          {en ? "View" : "Переглянути"}
                        </button>
                        <button
                          type="button"
                          className="sv2-cta sv2-secondary"
                          data-testid="sv2-doc-pdf"
                          onClick={() => {
                            const titleUk = String(doc.title_uk ?? doc.type ?? "Документ");
                            const documentNumber = doc.document_number ?? null;
                            const sourceUrl = doc.url!;
                            void mintCustomerDocumentViewUrl({ url: sourceUrl, lang }).then((r) => {
                              if (!r.ok) {
                                if (r.message) window.alert(r.message);
                                return;
                              }
                              setNativeReader(null);
                              setPdfViewer({
                                httpsUrl: r.httpsUrl,
                                titleUk,
                                documentNumber,
                                sourceUrl,
                              });
                            });
                          }}
                        >
                          PDF
                        </button>
                      </div>
                    ) : (
                      <div className="sv2-muted" style={{ fontSize: 12, marginTop: 4 }} data-testid="sv2-doc-not-ready">
                        {chip === "rejected"
                          ? en
                            ? "Could not generate this document. Try again later."
                            : "Не вдалося сформувати. Спробуйте пізніше."
                          : en
                            ? "Not ready yet."
                            : chipUk === "Готується"
                              ? "Готується"
                              : "Ще не сформовано"}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            </>
          ) : (
            <p className="sv2-muted" style={{ margin: 0, fontSize: 13 }} data-testid="sv2-documents-empty">
              {en
                ? "Documents are not ready yet."
                : "Документи ще не сформовані."}
            </p>
          )}
        </div>

      {hideFiscalBlock ? null : (
      <>
      <div className="sv2-section-title" style={{ marginTop: 16 }}>
        🧾 {en ? "Merchant fiscal receipt" : "Фіскальний чек продавця"}
      </div>
      <div
        className="sv2-info-card sv2-cabinet-card"
        data-testid="sv2-receipt-section"
        data-receipt-state={receipt.state}
        data-rail={
          receipt.state === "ready" || receipt.state === "fiscalized"
            ? "completed"
            : isUnpaid
              ? "awaiting"
              : statusRail
        }
      >
        <div>
          {receipt.state === "ready" || receipt.state === "fiscalized" || projection?.fiscal_state === "fiscalized"
            ? en
              ? "✓ Fiscal receipt created"
              : "✓ Фіскальний чек сформовано"
            : projection?.forming_receipt
              ? receiptStateLabel(lang, "forming")
              : projection?.awaiting_payment
                ? receiptStateLabel(lang, "awaiting_payment")
                : receiptStateLabel(lang, receipt.state)}
        </div>
        {receipt.test_badge ? (
          <div className="sv2-muted" style={{ fontSize: 12, marginTop: 4 }}>
            TEST
          </div>
        ) : null}
        {(receipt.state === "ready" || receipt.state === "fiscalized") && receipt.fiscal_number ? (
          <div className="sv2-muted" style={{ fontSize: 13, marginTop: 6 }}>
            {`${en ? "No." : "№"} ${receipt.fiscal_number}`}
            {receipt.issued_at ? ` · ${formatOrderDate(receipt.issued_at, lang)}` : null}
            {receipt.amount_uah != null ? ` · ${formatUah(receipt.amount_uah)}` : null}
          </div>
        ) : null}
        {(receipt.state === "ready" || receipt.state === "fiscalized") && receipt.receipt_url ? (
          <>
            <FiscalReceiptCardV2
              publicOrderNumber={String(order.display_order_number ?? orderId)}
              amountUah={receipt.amount_uah}
              issuedAt={receipt.issued_at ? formatOrderDate(receipt.issued_at, lang) : null}
              receiptUrl={receipt.receipt_url}
              fiscalNumber={receipt.fiscal_number}
              lang={en ? "en" : "uk"}
            />
          </>
        ) : null}
      </div>
      </>
      )}

      <div className="sv2-section-title" style={{ marginTop: 16 }} data-testid="sv2-lifecycle-timeline-title">
        {en ? "Order status" : "Статус замовлення"}
      </div>
      <div className="sv2-info-card sv2-cabinet-card" data-testid="sv2-lifecycle-timeline">
        {timeline && timeline.length > 0 ? (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {presentCustomerTimeline(timeline).map((ev, i) => (
              <li
                key={`${ev.created_at}-${ev.status}-${i}`}
                data-testid={`sv2-timeline-event-${i}`}
                data-active={ev.active ? "1" : "0"}
                style={{
                  padding: "8px 0",
                  borderTop: i === 0 ? "none" : "1px solid rgba(0,0,0,0.06)",
                  opacity: ev.active === false ? 0.75 : 1,
                }}
              >
                <div style={{ fontWeight: ev.active ? 700 : 600, fontSize: 14 }}>
                  {ev.title_uk ?? (en ? "Order update" : "Оновлення замовлення")}
                  {ev.active ? (
                    <span
                      className="sv2-muted sv2-timeline-current-badge"
                      data-testid="sv2-timeline-current-badge"
                    >
                      {en ? "Current" : "Поточний"}
                    </span>
                  ) : null}
                </div>
                {ev.explanation_uk && ev.explanation_uk !== ev.title_uk ? (
                  <div className="sv2-muted" style={{ fontSize: 13, marginTop: 2 }}>
                    {ev.explanation_uk}
                  </div>
                ) : null}
                <div className="sv2-muted" style={{ fontSize: 12, marginTop: 2 }}>
                  {ev.created_at ? formatOrderDate(ev.created_at, lang) : ""}
                  {ev.shipment_number != null
                    ? ` · ${en ? "Shipment" : "Відправлення"} №${ev.shipment_number}`
                    : ""}
                </div>
                {ev.receipt_link ? (
                  <button
                    type="button"
                    className="sv2-link"
                    style={{ marginTop: 4, fontSize: 13 }}
                    onClick={() => openHttpsSync(ev.receipt_link as string)}
                  >
                    {en ? "Open receipt" : "Відкрити чек"}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <div className="sv2-muted" style={{ fontSize: 13 }}>
            {en ? "No status updates yet." : "Подій статусу поки немає."}
          </div>
        )}
      </div>

      <div className="sv2-section-title" style={{ marginTop: 16 }}>
        🚚 {en ? "Delivery & tracking" : "Доставка і ТТН"}
      </div>
      <div
        className="sv2-info-card sv2-cabinet-card"
        data-testid="sv2-delivery-section"
        data-delivery-state={delivery.state}
        data-rail={hasTtn ? "shipping" : statusRail}
      >
        <div>{deliveryStateLabel(lang, delivery.state)}</div>
        {delivery.method || delivery.city ? (
          <div className="sv2-muted" style={{ fontSize: 13, marginTop: 6 }} data-testid="sv2-delivery-address">
            <div data-testid="sv2-delivery-method-human">
              🚚 {en ? "Delivery" : "Доставка"}
            </div>
            <div>{humanDeliveryMethodLabel(lang, delivery.method)}</div>
            {delivery.city ? <div>{delivery.city}</div> : null}
            {delivery.warehouse ? <div>{delivery.warehouse}</div> : null}
          </div>
        ) : null}
        {deliveryEdit.canEdit ? (
          <button
            type="button"
            className="sv2-cta sv2-secondary"
            style={{ marginTop: 10 }}
            data-testid="sv2-edit-delivery"
            onClick={() => {
              setEditCity(String(delivery.city ?? ""));
              setEditWarehouse(String(delivery.warehouse ?? ""));
              setEditName("");
              setDeliveryEditMsg(null);
              setDeliveryEditOpen(true);
            }}
          >
            {en ? "✏️ Edit delivery" : "✏️ Змінити доставку"}
          </button>
        ) : !isCancelledOrder ? (
          <button
            type="button"
            className="sv2-about-link"
            style={{ marginTop: 10, display: "inline-block" }}
            data-testid="sv2-edit-delivery-locked"
            onClick={onContactManager}
          >
            {en ? "Write to the manager" : "Написати менеджеру"}
          </button>
        ) : null}
        {!delivery.ttn_number &&
        (delivery.shipments ?? []).every((s) => !s.ttn_number) &&
        String((order as { shipping_requirement?: string | null }).shipping_requirement ?? "")
          .trim()
          .toLowerCase() === "no_ttn" ? (
          <div className="sv2-muted" style={{ fontSize: 13, marginTop: 8 }} data-testid="sv2-delivery-no-ttn">
            {en ? "Delivery: without TTN" : "Доставка: Без ТТН"}
          </div>
        ) : !delivery.ttn_number && (delivery.shipments ?? []).every((s) => !s.ttn_number) ? (
          <div className="sv2-muted" style={{ fontSize: 13, marginTop: 8 }} data-testid="sv2-delivery-ttn-pending">
            {en ? "TTN not created yet" : "ТТН ще не створено"}
          </div>
        ) : null}
        {(delivery.shipments ?? []).length > 0 ? (
          <div style={{ marginTop: 10 }} data-testid="sv2-delivery-shipments">
            {(delivery.shipments ?? []).map((ship) => (
              <div
                key={ship.shipment_id || `slot-${ship.slot_index}`}
                className="sv2-shipment-card"
                style={{
                  marginTop: 8,
                  paddingTop: 8,
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                }}
                data-testid={`sv2-shipment-slot-${ship.slot_index}`}
              >
                <div>
                  <strong>
                    {en ? `Shipment ${ship.shipment_number}` : `Відправлення №${ship.shipment_number}`}
                  </strong>
                </div>
                {ship.ttn_number ? (
                  <div style={{ marginTop: 4 }} data-testid={`sv2-delivery-ttn-${ship.slot_index}`}>
                    <strong>{en ? "TTN:" : "ТТН:"}</strong> {ship.ttn_number}
                  </div>
                ) : null}
                {(ship.items ?? []).length > 0 ? (
                  <ul
                    className="sv2-muted"
                    style={{ fontSize: 13, marginTop: 6, paddingLeft: 16 }}
                    data-testid={`sv2-shipment-items-${ship.slot_index}`}
                  >
                    {(ship.items ?? []).map((it, idx) => (
                      <li key={`${it.product_id}-${idx}`}>
                        {it.title}
                        {it.qty > 1 ? ` × ${it.qty}` : ""}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {ship.customer_visible_comment ? (
                  <div className="sv2-muted" style={{ fontSize: 13, marginTop: 4 }}>
                    {ship.customer_visible_comment}
                  </div>
                ) : null}
                {ship.status && !/^[a-z0-9_]+$/i.test(String(ship.status)) ? (
                  <div className="sv2-muted" style={{ fontSize: 13, marginTop: 4 }}>
                    {en ? "Status:" : "Статус:"} {ship.status}
                  </div>
                ) : null}
                {ship.updated_at ? (
                  <div className="sv2-muted" style={{ fontSize: 12, marginTop: 2 }}>
                    {en ? "Updated:" : "Оновлено:"} {formatOrderDate(ship.updated_at, lang)}
                  </div>
                ) : null}
                {ship.ttn_number ? (
                  <button
                    type="button"
                    className="sv2-cta sv2-secondary sv2-cta-compact"
                    style={{ display: "inline-block", marginTop: 6, fontSize: 13 }}
                    data-testid={`sv2-track-shipment-${ship.slot_index}`}
                    onClick={() => {
                      const timeline = Array.isArray(ship.timeline) ? ship.timeline : [];
                      const historyLocalOnly = !timeline.some((ev) =>
                        String(ev.event_type ?? "")
                          .toLowerCase()
                          .includes("np_tracking"),
                      );
                      setTrackingPanel({
                        ttn: ship.ttn_number!,
                        trackUrl: ship.track_url,
                        status: ship.status,
                        updatedAt: ship.updated_at,
                        slot: ship.slot_index,
                        timeline,
                        historyLocalOnly,
                      });
                    }}
                  >
                    {en ? "Route details" : "Деталі маршруту"}
                  </button>
                ) : null}
                {ship.timeline && ship.timeline.length > 0 ? (
                  <ul className="sv2-muted" style={{ fontSize: 12, marginTop: 6, paddingLeft: 16 }}>
                    {ship.timeline.slice(-5).map((ev, i) => (
                      <li key={`${ev.created_at}-${i}`}>
                        {ev.created_at} · {ev.event_type}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
        ) : delivery.state === "ttn_created" && delivery.ttn_number ? (
          <div style={{ marginTop: 10 }} data-testid="sv2-delivery-ttn">
            <div>
              <strong>{en ? "Tracking number (TTN):" : "Номер ТТН:"}</strong> {delivery.ttn_number}
            </div>
            {delivery.tracking_status ? (
              <div className="sv2-muted" style={{ fontSize: 13, marginTop: 4 }}>
                {delivery.tracking_status}
              </div>
            ) : null}
            <button
              type="button"
              className="sv2-cta sv2-secondary sv2-cta-compact"
              style={{ marginTop: 8 }}
              data-testid="sv2-track-shipment-legacy"
              onClick={() =>
                setTrackingPanel({
                  ttn: delivery.ttn_number!,
                  trackUrl: `https://novaposhta.ua/tracking/?cargo_number=${encodeURIComponent(delivery.ttn_number!)}`,
                  status: delivery.tracking_status,
                  updatedAt: null,
                  slot: 1,
                  timeline: [],
                  historyLocalOnly: true,
                })
              }
            >
              {en ? "Route details" : "Деталі маршруту"}
            </button>
          </div>
        ) : null}
      </div>

      {trackingPanel ? (
        <div
          className="sv2-sheet-overlay"
          role="dialog"
          aria-modal="true"
          data-testid="sv2-internal-tracking-panel"
          onClick={() => setTrackingPanel(null)}
        >
          <div
            className="sv2-info-card sv2-cabinet-card"
            style={{ margin: 16, maxWidth: 480 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sv2-section-title" style={{ marginTop: 0 }}>
              {en ? "Parcel tracking" : "Відстеження посилки"}
            </div>
            <div data-testid="sv2-tracking-ttn">
              <strong>ТТН:</strong> {trackingPanel.ttn}
            </div>
            {trackingPanel.status ? (
              <div className="sv2-muted" style={{ fontSize: 13, marginTop: 6 }} data-testid="sv2-tracking-status">
                {trackingPanel.status}
              </div>
            ) : null}
            {trackingPanel.updatedAt ? (
              <div className="sv2-muted" style={{ fontSize: 12, marginTop: 4 }}>
                {en ? "Updated:" : "Оновлено:"} {formatOrderDate(trackingPanel.updatedAt, lang)}
              </div>
            ) : null}
            <ul className="sv2-muted" style={{ fontSize: 12, marginTop: 10, paddingLeft: 16 }} data-testid="sv2-tracking-timeline">
              <li>
                {en ? "TTN attached to order" : "ТТН додано до замовлення"}
                {trackingPanel.slot ? ` · #${trackingPanel.slot}` : ""}
              </li>
              {trackingPanel.status ? <li>{trackingPanel.status}</li> : null}
              {trackingPanel.timeline
                .filter((ev) => String(ev.event_type ?? "").toLowerCase().includes("np_tracking"))
                .map((ev, i) => (
                  <li key={`np-${ev.created_at}-${i}`}>
                    {ev.created_at ? `${ev.created_at} · ` : ""}
                    {ev.event_type}
                  </li>
                ))}
            </ul>
            {trackingPanel.historyLocalOnly ? (
              <p
                className="sv2-muted"
                style={{ fontSize: 12, marginTop: 8 }}
                data-testid="sv2-tracking-history-local-note"
              >
                {en
                  ? "Tracking history in AsiaStyle is available from the moment of connection."
                  : "Історія відстеження в AsiaStyle доступна з моменту підключення"}
              </p>
            ) : null}
            <button
              type="button"
              className="sv2-cta"
              style={{ marginTop: 12, width: "100%" }}
              onClick={() => setTrackingPanel(null)}
            >
              {en ? "Close" : "Закрити"}
            </button>
            {trackingPanel.trackUrl ? (
              <a
                href={trackingPanel.trackUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="sv2-link"
                style={{ display: "inline-block", marginTop: 10, fontSize: 13 }}
                data-testid="sv2-tracking-external-np"
                onClick={(e) => {
                  e.preventDefault();
                  openHttpsSync(trackingPanel.trackUrl!);
                }}
              >
                {en ? "Open in Nova Poshta" : "Відкрити у Новій пошті"}
              </a>
            ) : null}
          </div>
        </div>
      ) : null}

      {order.payment_method === "bank_transfer_fop" ||
      (isUnpaid && !hideCustomerPaymentCta(displayState)) ? (
        <div data-testid="sv2-order-payment-panel" id="sv2-order-section-payment">
          <OrderPaymentPanelV2
            key={`${orderId}:${paymentMethodForPanel}:${order.payment_status}:${bankConfirmedAt ?? ""}`}
            lang={lang}
            sessionKey={sessionKey}
            orderId={orderId}
            displayOrderNumber={order.display_order_number}
            totalUah={order.total_uah}
            initialMethod={paymentMethodForPanel}
            allowMethodChange={false}
            suppressRecoveryActions
            orderPaymentStatus={order.payment_status}
            bankConfirmedAt={bankConfirmedAt}
            hideWhenPaid={isPaid}
            {...(isNovapayUnpaid ? { payCtaRef } : {})}
            {...(order.payment_method === "bank_transfer_fop" && !isPaid ? { fopReceiptCtaRef } : {})}
          />
        </div>
      ) : null}

        <CustomerOrderActionsBarV2
        lang={lang}
        plan={visiblePlan}
        handlers={{
          onContinuePayment: () => {
            emitEvent("continue_payment_clicked", { order_id: orderId });
            document.querySelector<HTMLElement>("[data-testid='sv2-order-payment-panel']")?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
            // Single invoke — do not also DOM-click (double session risk).
            if (payCtaRef.current) {
              payCtaRef.current();
              return;
            }
            document.querySelector<HTMLButtonElement>("[data-testid='sv2-novapay-pay-cta']")?.click();
          },
          onSendReceipt: () => {
            document.querySelector<HTMLElement>("[data-testid='sv2-order-payment-panel']")?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
            if (fopReceiptCtaRef.current) {
              fopReceiptCtaRef.current();
              return;
            }
            document.querySelector<HTMLButtonElement>("[data-testid='sv2-fop-send-receipt']")?.click();
          },
          onViewPaymentDetails: () => {
            document.querySelector<HTMLElement>("[data-testid='sv2-order-payment-panel']")?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          },
          onViewDelivery: () => {
            document
              .querySelector<HTMLElement>("[data-testid='sv2-delivery-section'], [data-testid='sv2-delivery-ttn']")
              ?.scrollIntoView({ behavior: "smooth", block: "start" });
          },
          onCheckPaymentStatus: () => void runStatusCheck(),
          onChangePaymentMethod: () => {
            setMethodSaveError(null);
            setMethodSheetOpen(true);
          },
          onAbandonPayment: () => void runAbandonPayment(),
          onCancelOrder: () => setCancelSheetOpen(true),
          onRequestCancellation: () => setCancelSheetOpen(true),
          onContactManager: () => {
            void customerOrderActionV2({ sessionKey, orderId, action: "manager_help" });
            onContactManager();
          },
          onRequestReturn: () => {
            void customerOrderActionV2({ sessionKey, orderId, action: "manager_help" });
            onContactManager();
          },
          onToCatalog: () => onGoHome(),
          busy: {
            check_payment_status: statusCheckBusy,
            change_payment_method: methodBusy,
            abandon_payment: abandonBusy,
            cancel_order: cancelBusy,
          },
        }}
      />
      {statusCheckMsg ? <div className="sv2-muted" style={{ fontSize: 13 }}>{statusCheckMsg}</div> : null}
      {methodMsg ? <div className="sv2-alert sv2-alert-err">{methodMsg}</div> : null}
      {abandonMsg ? <div className="sv2-alert sv2-alert-ok">{abandonMsg}</div> : null}
      {actionPlan.actions.includes("request_return") ? (
        <p className="sv2-muted" style={{ fontSize: 12, marginTop: 8 }}>
          {en
            ? "Food return eligibility is decided by the manager under store policy — not automatic."
            : "Можливість повернення харчових товарів визначає менеджер за політикою магазину — не автоматично."}
        </p>
      ) : null}

      {cancelledNow ? (
        <div className="sv2-alert sv2-alert-ok" data-testid="sv2-order-cancelled-msg" style={{ marginTop: 12 }}>
          {en ? "Order cancelled." : "Замовлення скасовано."}
        </div>
      ) : null}
      {cancelRequestedNow ? (
        <div className="sv2-alert sv2-alert-ok" data-testid="sv2-cancel-requested-msg" style={{ marginTop: 12 }}>
          {en
            ? "Cancellation request sent. Payment stays confirmed until a manager reviews a refund."
            : "Запит надіслано. Оплата залишається підтвердженою, поки менеджер не перевірить повернення."}
        </div>
      ) : null}
      {cancelBlockedMsg ? (
        <div className="sv2-alert sv2-alert-err" data-testid="sv2-cancel-blocked-msg" style={{ marginTop: 12 }}>
          {cancelBlockedMsg}
        </div>
      ) : null}

      {cancelSheetOpen ? (
        <div className="sv2-modal-overlay" role="dialog" aria-modal="true" data-testid="sv2-cancel-order-sheet">
          <button
            type="button"
            className="sv2-sheet-backdrop"
            aria-label={en ? "Close" : "Закрити"}
            onClick={() => setCancelSheetOpen(false)}
          />
          <div className="sv2-sheet-panel">
            <div className="sv2-sheet-handle" aria-hidden="true" />
            <div className="sv2-section-title">
              {isPaid
                ? en
                  ? "Request cancellation?"
                  : "Запросити скасування?"
                : en
                  ? "Cancel order?"
                  : "Скасувати замовлення?"}
            </div>
            <p className="sv2-muted" data-testid="sv2-cancel-order-body">
              {isPaid
                ? en
                  ? "Payment is already confirmed. A manager will review the order and, if needed, agree a refund. The payment status will not be changed automatically."
                  : "Оплата вже підтверджена. Менеджер перевірить замовлення та, якщо потрібно, узгодить повернення коштів."
                : en
                  ? "The order is still unpaid and has not been sent to packing. After cancellation you will not be able to pay for it."
                  : "Замовлення ще не оплачено і не передано на комплектацію. Після скасування оплатити його вже не вийде."}
            </p>
            <label className="sv2-reason-label" htmlFor="sv2-cancel-reason">
              {en ? "Reason" : "Причина"}
            </label>
            <div className="sv2-reason-chips" data-testid="sv2-cancel-reason-chips">
              {REASON_CHIPS.map((chip) => (
                <button
                  key={chip.code}
                  type="button"
                  className={`sv2-reason-chip${selectedReasonChip === chip.code ? " is-active" : ""}`}
                  data-reason-code={chip.code}
                  onClick={() => setSelectedReasonChip(chip.code === selectedReasonChip ? null : chip.code)}
                >
                  {chip.label}
                </button>
              ))}
            </div>
            {rescue === "cart" ? (
              <button
                type="button"
                className="sv2-cta sv2-secondary"
                data-testid="sv2-cancel-rescue-cart"
                onClick={() => {
                  setCancelSheetOpen(false);
                  onGoHome();
                }}
              >
                {en ? "Back to cart" : "Повернутися до кошика"}
              </button>
            ) : null}
            {rescue === "delivery" ? (
              deliveryEdit.canEdit ? (
                <button
                  type="button"
                  className="sv2-cta sv2-secondary"
                  data-testid="sv2-cancel-rescue-delivery"
                  onClick={() => {
                    setCancelSheetOpen(false);
                    setEditCity(String(delivery.city ?? ""));
                    setEditWarehouse(String(delivery.warehouse ?? ""));
                    setEditName("");
                    setDeliveryEditOpen(true);
                  }}
                >
                  {en ? "Edit delivery instead" : "Змінити доставку замість скасування"}
                </button>
              ) : (
                <p className="sv2-muted" data-testid="sv2-cancel-rescue-delivery" style={{ fontSize: 13 }}>
                  {en
                    ? "Write the manager to change delivery instead of cancelling."
                    : "Напишіть менеджеру, щоб змінити доставку, замість скасування."}
                </p>
              )
            ) : null}
            {rescue === "payment_help" ? (
              <button
                type="button"
                className="sv2-cta sv2-secondary"
                data-testid="sv2-cancel-rescue-payment"
                onClick={() => {
                  setCancelSheetOpen(false);
                  void customerOrderActionV2({ sessionKey, orderId, action: "manager_help" });
                  onContactManager();
                }}
              >
                {en ? "Payment help / contact manager" : "Допомога з оплатою / менеджер"}
              </button>
            ) : null}
            <textarea
              id="sv2-cancel-reason"
              className="sv2-manual-copy sv2-reason-input"
              placeholder={en ? "Add details (optional)" : "Додайте деталі (необовʼязково)"}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              data-testid="sv2-cancel-reason-input"
            />
            <button
              type="button"
              className="sv2-cta sv2-secondary"
              onClick={() => setCancelSheetOpen(false)}
              data-testid="sv2-cancel-order-keep"
            >
              {en ? "Keep order" : "Залишити замовлення"}
            </button>
            <button
              type="button"
              className="sv2-cta sv2-destructive"
              style={{ marginTop: 8 }}
              disabled={cancelBusy}
              onClick={() => void runCancel()}
              data-testid="sv2-cancel-order-confirm"
            >
              {cancelBusy
                ? en
                  ? "Sending…"
                  : "Надсилаємо…"
                : isPaid
                  ? en
                    ? "Yes, request cancellation"
                    : "Так, скасувати"
                  : en
                    ? "Yes, cancel"
                    : "Так, скасувати"}
            </button>
          </div>
        </div>
      ) : null}

      {deliveryEditOpen ? (
        <div className="sv2-modal-overlay" role="dialog" aria-modal="true" data-testid="sv2-delivery-edit-sheet">
          <div className="sv2-modal-card">
            <h3>{en ? "Edit delivery" : "Змінити доставку"}</h3>
            <label className="sv2-field">
              <span>{en ? "Recipient" : "Отримувач"}</span>
              <input
                className="sv2-input"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                data-testid="sv2-delivery-edit-name"
              />
            </label>
            <label className="sv2-field">
              <span>{en ? "City" : "Місто"}</span>
              <input
                className="sv2-input"
                value={editCity}
                onChange={(e) => setEditCity(e.target.value)}
                data-testid="sv2-delivery-edit-city"
              />
            </label>
            <label className="sv2-field">
              <span>{en ? "Warehouse / postomat" : "Відділення / поштомат"}</span>
              <input
                className="sv2-input"
                value={editWarehouse}
                onChange={(e) => setEditWarehouse(e.target.value)}
                data-testid="sv2-delivery-edit-warehouse"
              />
            </label>
            {deliveryEditMsg ? (
              <div className="sv2-alert" style={{ marginTop: 8 }}>
                {deliveryEditMsg}
              </div>
            ) : null}
            <button
              type="button"
              className="sv2-cta"
              disabled={deliveryEditBusy}
              data-testid="sv2-delivery-edit-save"
              onClick={() => {
                void (async () => {
                  setDeliveryEditBusy(true);
                  setDeliveryEditMsg(null);
                  const res = await updateCustomerDeliveryV2({
                    sessionKey,
                    orderId,
                    contactName: editName,
                    deliveryCity: editCity,
                    deliveryWarehouse: editWarehouse,
                  });
                  setDeliveryEditBusy(false);
                  if (!res.ok) {
                    setDeliveryEditMsg(res.message ?? (en ? "Could not save" : "Не вдалося зберегти"));
                    return;
                  }
                  setDeliveryEditOpen(false);
                  invalidateOrderDetailCache(sessionKey, orderId);
                  setAttempt((n) => n + 1);
                })();
              }}
            >
              {deliveryEditBusy ? (en ? "Saving…" : "Зберігаємо…") : en ? "Save" : "Зберегти"}
            </button>
            <button
              type="button"
              className="sv2-cta sv2-secondary"
              style={{ marginTop: 8 }}
              onClick={() => setDeliveryEditOpen(false)}
            >
              {en ? "Cancel" : "Скасувати"}
            </button>
          </div>
        </div>
      ) : null}

      {allowedMethodsCount > 1 ? (
      <PaymentMethodChangeSheetV2
        lang={lang}
        open={methodSheetOpen}
        currentMethod={order.payment_method}
        paymentStatus={order.payment_status}
        deliveryMethod={order.delivery_method}
        deliveryCity={order.delivery_city ?? delivery.city}
        deliveryWarehouse={order.delivery_warehouse ?? delivery.warehouse}
        onlinePaymentEnabled={!isNovapayCheckoutDegradedStaging()}
        orderStatus={order.order_top_status ?? order.status}
        busy={methodBusy}
        saveError={methodSaveError}
        onClose={() => {
          setMethodSheetOpen(false);
          setMethodSaveError(null);
        }}
        onContactManager={() => {
          void customerOrderActionV2({ sessionKey, orderId, action: "manager_help" });
          onContactManager();
        }}
        onSave={(next, meta) => void runChangeMethod(next, meta)}
      />
      ) : null}

      {processingConfirmOpen && pendingMethodChange ? (
        <div className="sv2-sheet-backdrop" data-testid="sv2-processing-abandon-confirm">
          <div className="sv2-sheet">
            <strong>{t(lang, "confirm_abandon_processing_title")}</strong>
            <p>{t(lang, "confirm_abandon_processing_body")}</p>
            <button
              type="button"
              className="sv2-cta sv2-secondary"
              onClick={() => {
                setProcessingConfirmOpen(false);
                setPendingMethodChange(null);
              }}
            >
              {t(lang, "confirm_abandon_processing_no")}
            </button>
            <button
              type="button"
              className="sv2-cta"
              style={{ marginTop: 8 }}
              disabled={methodBusy}
              onClick={() =>
                void runChangeMethod(pendingMethodChange.next, pendingMethodChange.meta, {
                  confirmProcessingAbandon: true,
                })
              }
            >
              {t(lang, "confirm_abandon_processing_yes")}
            </button>
          </div>
        </div>
      ) : null}

      <div className="sv2-order-detail-actions sv2-order-detail-actions-stack">
        <button
          type="button"
          className="sv2-cta sv2-action-secondary"
          data-testid="sv2-repeat-order-btn"
          onClick={() => {
            emitEvent("repeat_order_clicked", { order_id_present: true });
            onRepeatOrder({ order, items });
          }}
        >
          {en ? "Repeat order" : "Повторити замовлення"}
        </button>
      </div>

      <div className="sv2-order-detail-referral" data-testid="sv2-order-detail-referral" style={{ marginTop: 16 }}>
        <ReferralCardV2
          lang={lang}
          referral={referral}
          surface="cabinet"
          compact
          {...(onReferralShared ? { onShared: onReferralShared } : {})}
        />
      </div>
      {nativeReader ? (
        <DocumentNativeReader
          lang={lang}
          view={nativeReader.view}
          loading={nativeReader.loading}
          error={nativeReader.error}
          testIdPrefix="sv2-doc-reader"
          onBack={() => setNativeReader(null)}
          onDownloadPdf={() => {
            void openCustomerDocumentPdf({
              url: nativeReader.sourceUrl,
              mode: "download",
              filename: `${nativeReader.documentNumber || "document"}.pdf`,
              lang,
            });
          }}
          onOpenPdfMode={() => {
            void mintCustomerDocumentViewUrl({ url: nativeReader.sourceUrl, lang }).then((r) => {
              if (!r.ok) {
                if (r.message) window.alert(r.message);
                return;
              }
              setPdfViewer({
                httpsUrl: r.httpsUrl,
                titleUk: nativeReader.titleUk,
                documentNumber: nativeReader.documentNumber,
                sourceUrl: nativeReader.sourceUrl,
              });
              setNativeReader(null);
            });
          }}
          onCopyNumber={() => {
            const n = String(nativeReader.documentNumber ?? "").trim();
            if (n && typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
              void navigator.clipboard.writeText(n);
            }
          }}
        />
      ) : null}
      {pdfViewer ? (
        <DocumentPdfViewerV2
          httpsUrl={pdfViewer.httpsUrl}
          titleUk={pdfViewer.titleUk}
          documentNumber={pdfViewer.documentNumber}
          lang={lang}
          onBack={() => setPdfViewer(null)}
          onDownload={() => {
            void openCustomerDocumentPdf({
              url: pdfViewer.sourceUrl,
              mode: "download",
              filename: `${pdfViewer.documentNumber || "document"}.pdf`,
              lang,
            });
          }}
          onOpenExternal={() => {
            void openCustomerDocumentPdf({
              url: pdfViewer.sourceUrl,
              mode: "inline",
              filename: `${pdfViewer.documentNumber || "document"}.pdf`,
              lang,
            });
          }}
          onCopyNumber={() => {
            const n = String(pdfViewer.documentNumber ?? "").trim();
            if (n && typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
              void navigator.clipboard.writeText(n);
            }
          }}
        />
      ) : null}
    </div>
  );
}
